import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "@/i18n/I18nContext";
import { ChartLineUp, Notebook, Shield, Sparkle } from "@phosphor-icons/react";

const STORAGE_KEY = "trademind_onboarded";

export const shouldShowOnboarding = () => !localStorage.getItem(STORAGE_KEY);

const OnboardingGuide = ({ onClose }) => {
  const { lang } = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const cs = lang === "cs";

  const steps = [
    {
      icon: Sparkle,
      title: cs ? "Vítej v TradeMind AI! 👋" : "Welcome to TradeMind AI! 👋",
      text: cs
        ? "Tvůj AI parťák pro trading. Za minutu ti ukážeme, jak vytěžit maximum — analýzu grafu, deník a ochranu prop firm účtu."
        : "Your AI trading copilot. In one minute we'll show you the essentials — chart analysis, journal and prop firm protection.",
    },
    {
      icon: ChartLineUp,
      title: cs ? "1 · AI analýza grafu" : "1 · AI chart analysis",
      text: cs
        ? "Vyfoť nebo ulož screenshot grafu (TradingView, MT4/5, Binance…) a nahraj ho v sekci Analýza. AI ti do pár sekund vrátí signál BUY/SELL/WAIT, vstup, stop loss i take profity."
        : "Take a screenshot of any chart (TradingView, MT4/5, Binance…) and upload it in Analyze. AI returns a BUY/SELL/WAIT signal with entry, stop loss and take profits in seconds.",
    },
    {
      icon: Notebook,
      title: cs ? "2 · Obchodní deník" : "2 · Trading journal",
      text: cs
        ? "Zapisuj obchody ručně, nebo nahraj screenshot historie a AI je importuje za tebe (OCR). Vytvoř si složky podle prop firem — statistiky se počítají automaticky."
        : "Log trades manually, or upload a screenshot of your history and AI imports them for you (OCR). Create folders per prop firm — stats compute automatically.",
    },
    {
      icon: Shield,
      title: cs ? "3 · Prop Firm Guardian" : "3 · Prop Firm Guardian",
      text: cs
        ? "Přidej svůj prop firm účet a deník ho začne živě hlídat — denní ztrátu, drawdown i cíl. V Nastavení si zapni chytré alerty, ať tě appka varuje včas."
        : "Add your prop firm account and the journal watches it live — daily loss, drawdown and target. Enable smart alerts in Settings to get warned in time.",
    },
  ];

  const done = (goAnalyze) => {
    localStorage.setItem(STORAGE_KEY, "1");
    onClose();
    if (goAnalyze) navigate("/app/analyze");
  };

  const s = steps[step];
  const Icon = s.icon;
  const last = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-guide">
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-md overflow-hidden">
        <div className="h-1 bg-zinc-900">
          <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
        <div className="p-6 sm:p-8 text-center">
          <div className="w-14 h-14 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center mx-auto mb-4">
            <Icon weight="bold" size={26} className="text-blue-400" />
          </div>
          <h3 className="text-xl font-medium text-zinc-50" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="onboarding-title">{s.title}</h3>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{s.text}</p>
          <div className="flex items-center justify-center gap-1.5 mt-6">
            {steps.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === step ? "bg-blue-500" : "bg-zinc-700"}`} />
            ))}
          </div>
          <div className="mt-6 space-y-2">
            {!last ? (
              <button onClick={() => setStep(step + 1)} data-testid="onboarding-next" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-md text-sm transition-colors">
                {lang === "cs" ? "Další" : "Next"}
              </button>
            ) : (
              <>
                <button onClick={() => done(true)} data-testid="onboarding-start-analysis" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-md text-sm transition-colors">
                  {lang === "cs" ? "Spustit první analýzu 🚀" : "Run my first analysis 🚀"}
                </button>
                <button onClick={() => done(false)} data-testid="onboarding-finish" className="w-full text-zinc-400 hover:text-zinc-100 px-4 py-2 rounded-md text-sm transition-colors">
                  {lang === "cs" ? "Hotovo, rozhlédnu se sám" : "Done, I'll explore myself"}
                </button>
              </>
            )}
            {!last && (
              <button onClick={() => done(false)} data-testid="onboarding-skip" className="w-full text-zinc-500 hover:text-zinc-200 px-4 py-1.5 text-xs transition-colors">
                {lang === "cs" ? "Přeskočit průvodce" : "Skip guide"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingGuide;
